import PropTypes from 'prop-types';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';

import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// ======================================================

const generateHandler = (filters, onFilters, option) => (newValue, interactionType) => {
  if (interactionType === 'checkbox') {
    const updatedFilters = (filters[option] || []).includes(newValue)
      ? filters[option].filter((value) => value !== newValue)
      : [...(filters[option] || []), newValue];
    onFilters(option, updatedFilters);
  } else {
    onFilters(option, newValue);
  }
};

// ======================================================

const renderFilterSection = (title, options, selectedValues, onChangeHandler, interactionType) => {
  const isCheckbox = options.length <= 5;

  return (
    <Stack>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>

      {isCheckbox ? (
        options.map((option) => (
          <FormControlLabel
            key={option}
            control={
              <Checkbox
                checked={selectedValues.includes(option)}
                onClick={() => onChangeHandler(option, interactionType)}
              />
            }
            label={option}
          />
        ))
      ) : (
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={options.filter((opt) => opt !== null && opt !== undefined && opt !== '')}
          value={selectedValues}
          onChange={(event, newValue) => onChangeHandler(newValue, interactionType)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                selectedValues?.length === 0 && !params?.inputProps?.value ? `Select ${title}` : ''
              }
            />
          )}
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={index}
                label={option}
                size="small"
                variant="soft"
              />
            ))
          }
        />
      )}
    </Stack>
  );
};

// ======================================================

function CommonFilter({
  open,
  onOpen,
  onClose,
  filters,
  onFilters,
  canReset,
  onResetFilters,
  filterSections,
}) {
  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 2, pr: 1, pl: 2.5 }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Filters
      </Typography>

      <Tooltip title="Reset">
        <IconButton onClick={onResetFilters}>
          <Badge color="error" variant="dot" invisible={!canReset}>
            <Iconify icon="solar:restart-bold" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title="Close">
        <IconButton onClick={onClose}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <>
      <Button
        disableRipple
        color="inherit"
        endIcon={
          <Badge color="error" variant="dot" invisible={!canReset}>
            <Iconify icon="ic:round-filter-list" />
          </Badge>
        }
        onClick={onOpen}
      >
        Filters
      </Button>

      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 280 } }}
      >
        {renderHead}
        <Divider />

        <Scrollbar sx={{ px: 2.5, py: 5 }}>
          <Stack spacing={5}>
            {/* ✅ NORMAL FILTERS */}
            {filterSections.map(({ title, options, filterKey }) => {
              // 🔥 FIX: skip date filters
              if (filterKey === 'StartDate' || filterKey === 'EndDate') {
                return null;
              }

              const selectedValues = filters[filterKey] || [];
              const interactionType = options.length <= 5 ? 'checkbox' : 'autocomplete';

              return renderFilterSection(
                title,
                options,
                selectedValues,
                generateHandler(filters, onFilters, filterKey),
                interactionType
              );
            })}

            {/* ✅ START DATE */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack spacing={2}>
                <Typography variant="subtitle2">Start Date</Typography>

                <DatePicker
                  label="From"
                  format="DD-MM-YYYY"
                  value={filters.StartDate?.[0] ? dayjs(filters.StartDate[0]) : null}
                  onChange={(newValue) => {
                    onFilters('StartDate', [
                      newValue ? newValue.toISOString() : null,
                      filters.StartDate?.[1] || null,
                    ]);
                  }}
                />

                <DatePicker
                  label="To"
                  format="DD-MM-YYYY"
                  value={filters.StartDate?.[1] ? dayjs(filters.StartDate[1]) : null}
                  onChange={(newValue) => {
                    onFilters('StartDate', [
                      filters.StartDate?.[0] || null,
                      newValue ? newValue.toDate() : null,
                    ]);
                  }}
                />
              </Stack>
            </LocalizationProvider>

            {/* ✅ END DATE */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack spacing={2}>
                <Typography variant="subtitle2">End Date</Typography>

                <DatePicker
                  label="From"
                  format="DD-MM-YYYY"
                  value={filters.EndDate?.[0] ? dayjs(filters.EndDate[0]) : null}
                  onChange={(newValue) => {
                    onFilters('EndDate', [
                      newValue ? newValue.toISOString() : null,
                      filters.EndDate?.[1] || null,
                    ]);
                  }}
                />

                <DatePicker
                  label="To"
                  format="DD-MM-YYYY"
                  value={filters.EndDate?.[1] ? dayjs(filters.EndDate[1]) : null}
                  onChange={(newValue) => {
                    onFilters('EndDate', [
                      filters.EndDate?.[0] || null,
                      newValue ? newValue.toDate() : null,
                    ]);
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}

CommonFilter.propTypes = {
  canReset: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onFilters: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  filterSections: PropTypes.array.isRequired,
};

export default CommonFilter;
